import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  TextField,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { useNavigate } from "react-router-dom";

import { listAdmissions } from "../api/admissionsApi";
import { listHospitals } from "../api/hospitalsApi";
import { prettyError } from "../utils/prettyError";
import { Colors } from "../../utils/Colors";
import { ROUTES } from "../../shared/constants/routes";

function pickFirst(...values) {
  return values.find((v) => v !== undefined && v !== null && v !== "");
}

function fmt(v) {
  if (v === undefined || v === null || v === "") return "—";
  return String(v);
}

function getHospitalId(h) {
  if (!h) return null;
  if (typeof h !== "object") return h;
  return h.id ?? h.pk ?? h.uuid ?? h.hospital_id ?? h.organization_id ?? null;
}

function getHospitalName(h) {
  if (!h) return null;
  if (typeof h !== "object") return String(h);
  return h.name ?? h.hospital_name ?? h.organization_name ?? h.title ?? null;
}

function makeHospitalMap(rows) {
  const map = {};
  (rows || []).forEach((row) => {
    const id = getHospitalId(row);
    if (id !== null && id !== undefined) {
      map[String(id)] = row;
    }
  });
  return map;
}

function normalizeAdmission(row, hospitalsMap) {
  const hospitalRef =
    row?.organization ??
    row?.organization_id ??
    row?.hospital ??
    row?.hospital_id ??
    null;

  const hospitalObj =
    typeof hospitalRef === "object" && hospitalRef !== null
      ? hospitalRef
      : hospitalsMap[String(hospitalRef)] || null;

  return {
    admissionId: pickFirst(row?.admission_id, row?.id, row?.pk, row?.uuid),
    admissionNo: pickFirst(
      row?.admission_no,
      row?.admission_number,
      row?.admission_code
    ),
    patientName: pickFirst(row?.patient_name, row?.full_name),
    hospitalName: pickFirst(
      row?.organization_name,
      row?.hospital_name,
      getHospitalName(hospitalObj)
    ),
  };
}

export default function AdmissionListSmart() {
  const navigate = useNavigate();

  const [rows, setRows] = React.useState([]);
  const [filteredRows, setFilteredRows] = React.useState([]);
  const [search, setSearch] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  async function loadData() {
    setLoading(true);
    setError("");

    try {
      const [admissions, hospitals] = await Promise.all([
        listAdmissions(),
        listHospitals(),
      ]);

      const hospitalsMap = makeHospitalMap(hospitals || []);
      const normalized = (admissions || []).map((row) =>
        normalizeAdmission(row, hospitalsMap)
      );

      setRows(normalized);
      setFilteredRows(normalized);
    } catch (e) {
      setError(prettyError(e));
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    loadData();
  }, []);

  React.useEffect(() => {
    const q = String(search || "").trim().toLowerCase();

    if (!q) {
      setFilteredRows(rows);
      return;
    }

    setFilteredRows(
      rows.filter((r) =>
        [r.admissionNo, r.patientName, r.hospitalName]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      )
    );
  }, [search, rows]);

  function handleRowClick(row) {
    if (!row?.admissionId) return;
    navigate(ROUTES.ADMISSIONS_DETAILS(row.admissionId));
  }

  return (
    <Box sx={{ p: 2, background: Colors.bg.app, minHeight: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Card
          variant="outlined"
          sx={{
            width: "100%",
            maxWidth: 1000,
            borderRadius: 3,
            borderColor: Colors.border.light,
            background: Colors.bg.paper,
            boxShadow: Colors.shadow.brand,
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 900, color: Colors.text.title }}>
              Admission List
            </Typography>

            <Typography sx={{ mt: 0.5, fontSize: 13, color: Colors.text.sub }}>
              Showing Admission Number, Patient Name, and Hospital Name.
            </Typography>

            <Divider sx={{ my: 2, borderColor: Colors.border.topLine }} />

            {error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : null}

            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                borderColor: Colors.border.light,
              }}
            >
              <Box sx={{ p: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by admission number, patient, hospital..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlinedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Divider />

              {loading ? (
                <Box
                  sx={{
                    p: 3,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CircularProgress size={22} />
                  <Typography>Loading admissions...</Typography>
                </Box>
              ) : filteredRows.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <Alert severity="info">No admissions found.</Alert>
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 560 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 900 }}>Admission No</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Patient Name</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Hospital Name</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredRows.map((row, idx) => (
                        <TableRow
                          key={`${row?.admissionId ?? idx}`}
                          hover
                          onClick={() => handleRowClick(row)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { backgroundColor: Colors.bg.hover },
                          }}
                        >
                          <TableCell>{fmt(row.admissionNo)}</TableCell>
                          <TableCell>{fmt(row.patientName)}</TableCell>
                          <TableCell>{fmt(row.hospitalName)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}